import { Box } from '@mui/material'
import React from 'react'
import { ActionButton } from '../../action-button'
import { ThemeButton } from '../../theme-button'

/**
 * @typedef {object} IconButtonConfig
 * @property {string} label - El texto que se mostrará junto al icono.
 * @property {React.ReactNode | function(): React.ReactNode} icon - El icono a mostrar.
 * @property {function(React.MouseEvent<HTMLElement>): void} onClick - La función que se ejecutará cuando se haga clic en el botón.
 * @property {boolean} [hidden] - Si es `true`, el botón no se renderizará.
 */

/**
 * @typedef {object} FullButtonConfig
 * @property {string} label - El texto que se mostrará en el botón.
 * @property {function(React.MouseEvent<HTMLElement>): void} [onClick] - La función que se ejecutará cuando se haga clic en el botón.
 * @property {boolean} [hidden] - Si es `true`, el botón no se renderizará.
 */

/**
 * @typedef {object} HeaderButtonsProps
 * @property {IconButtonConfig[]} [iconButtons=[]] - Un array de configuraciones para botones con icono (renderizados como `ActionButton`).
 * @property {FullButtonConfig[]} [fullButtons=[]] - Un array de configuraciones para botones completos (renderizados como `ThemeButton`).
 */

/**
 * Un componente de layout que organiza botones en una cabecera, divididos en dos grupos:
 * botones con icono a la izquierda y botones completos a la derecha.
 * 
 * Los botones con icono (`iconButtons`) se renderizan usando `ActionButton` y ocupan el lado
 * izquierdo con crecimiento flexible. Los botones completos (`fullButtons`) se renderizan
 * usando `ThemeButton` y se alinean a la derecha.
 * 
 * @component
 * @example
 * import { Add as AddIcon, Edit as EditIcon } from '@mui/icons-material';
 * 
 * const MyPageHeader = () => {
 *   const iconButtons = [
 *     {
 *       label: 'Añadir',
 *       icon: <AddIcon />,
 *       onClick: () => console.log('Añadir clickeado'),
 *     },
 *     {
 *       label: 'Editar',
 *       icon: <EditIcon />,
 *       onClick: () => console.log('Editar clickeado'),
 *       hidden: !hasEditPermission,
 *     },
 *   ];
 * 
 *   const fullButtons = [
 *     {
 *       label: 'Guardar',
 *       onClick: handleSave,
 *     },
 *     {
 *       label: 'Cancelar',
 *       onClick: handleCancel,
 *     },
 *   ];
 * 
 *   return <HeaderButtons iconButtons={iconButtons} fullButtons={fullButtons} />;
 * };
 * 
 * @param {HeaderButtonsProps} props - Las props del componente HeaderButtons.
 */
export const HeaderButtons = ({ iconButtons = [], fullButtons = [] }) => {
  return (
    <Box display="flex" flexWrap="wrap" width="100%">
      {iconButtons.length ? (
        <Box display="flex" flexGrow={2} flexWrap="wrap">
          {iconButtons.map((button, i) =>
            !button['hidden'] ? <ActionButton key={i} {...button} /> : null
          )}
        </Box>
      ) : null}
      {fullButtons.length ? (
        <Box display="flex" flexGrow={0.5} justifyContent="flex-end">
          {fullButtons.map((button, i) => {
            return (
              <ThemeButton key={i} color="primary" labelM="sm" {...button}>
                {button.label}
              </ThemeButton>
            )
          })}
        </Box>
      ) : null}
    </Box>
  )
}

export default HeaderButtons
